import { toast } from 'react-toastify';
import ApiService from '../services/apiService';
import ExternalApiService from '../services/externalApiService';
import ModernAIService from '../services/modernAIService';
import MCQService from '../services/mcqService';
import ShortAnswerService from '../services/shortAnswerService';
import ImageAnalysisService from '../services/imageAnalysisService';
// Legacy services for fallback
import GeminiDirectService from '../services/geminiDirectService';
import AudioService from '../services/audioService';

class WorkflowEngine {
  constructor() {
    this.executionResults = new Map();
    this.isRunning = false;
    this.apiService = new ApiService();
    this.externalApiService = new ExternalApiService();
    // Primary modern AI service
    this.modernAIService = new ModernAIService();
    // New specialized services
    this.mcqService = new MCQService();
    this.shortAnswerService = new ShortAnswerService();
    this.imageAnalysisService = new ImageAnalysisService();
    // Legacy services for fallback
    this.geminiDirectService = new GeminiDirectService();
    this.audioService = new AudioService();
  }

  async executeWorkflow(nodes, edges) {
    if (this.isRunning) {
      toast.warning('Workflow is already running!');
      return;
    }

    this.isRunning = true;
    this.executionResults.clear();
    
    // Calculate estimated time based on node types
    const estimatedTime = this.formatEstimatedTime(nodes, edges);
    const startTime = Date.now();
    
    try {
      toast.info(`🚀 Starting workflow execution... Estimated time: ${estimatedTime}`, { autoClose: 5000 });
      
      // Build execution graph
      const executionGraph = this.buildExecutionGraph(nodes, edges);
      
      // Execute nodes in topological order with progress tracking
      const results = await this.executeNodesWithProgress(executionGraph, nodes.length);
      
      const actualTime = Math.round((Date.now() - startTime) / 1000);
      toast.success(`🎉 Workflow completed in ${actualTime}s! All blocks executed successfully.`, { autoClose: 8000 });
      return results;
      
    } catch (error) {
      console.error('Workflow execution failed:', error);
      toast.error(`❌ Workflow failed: ${error.message}`);
      throw error;
    } finally {
      this.isRunning = false;
    }
  }

  // Calculate estimated execution time based on node types
  calculateEstimatedTime(nodes, edges) {
    const timeEstimates = {
      'prompt': 1,         // 1 second
      'textGen': 5,        // 5 seconds
      'visual': 15,        // 15 seconds (can be longer with Imagen)
      'worksheet': 10,     // 10 seconds
      'mcq': 4,           // 4 seconds (faster than full worksheet)
      'shortAnswer': 4,    // 4 seconds (faster than full worksheet)
      'imageAnalysis': 6,  // 6 seconds (image processing)
      'audio': 6,         // 6 seconds (30-second clips, faster)
      'upload': 2,        // 2 seconds
      'input': 1,         // 1 second
      'api': 3,           // 3 seconds
      'offlineLLM': 7     // 7 seconds
    };
    
    let totalSeconds = 0;
    nodes.forEach(node => {
      totalSeconds += timeEstimates[node.type] || 5;
    });
    
    // Add buffer time
    totalSeconds = Math.ceil(totalSeconds * 1.2);
    
    // Ensure we always return a number
    return Number(totalSeconds) || 0;
  }
  
  // Format estimated time for display
  formatEstimatedTime(nodes, edges) {
    const totalSeconds = this.calculateEstimatedTime(nodes, edges);
    
    // Ensure we have a valid number
    const validSeconds = Number(totalSeconds) || 0;
    
    if (validSeconds < 60) {
      return `${validSeconds}s`;
    } else {
      const minutes = Math.floor(validSeconds / 60);
      const seconds = validSeconds % 60;
      return `${minutes}m ${seconds}s`;
    }
  }

  buildExecutionGraph(nodes, edges) {
    const graph = new Map();
    const inDegree = new Map();
    
    // Initialize nodes
    nodes.forEach(node => {
      graph.set(node.id, {
        node,
        dependencies: [],
        dependents: []
      });
      inDegree.set(node.id, 0);
    });
    
    // Build edges
    edges.forEach(edge => {
      const sourceNode = graph.get(edge.source);
      const targetNode = graph.get(edge.target);
      
      if (sourceNode && targetNode) {
        sourceNode.dependents.push(edge.target);
        targetNode.dependencies.push(edge.source);
        inDegree.set(edge.target, inDegree.get(edge.target) + 1);
      }
    });
    
    return { graph, inDegree };
  }

  async executeNodesWithProgress(executionGraph, totalNodes, callbacks = {}) {
    const { graph, inDegree } = executionGraph;
    const queue = [];
    const results = new Map();
    let completedNodes = 0;
    
    // Extract callbacks
    const { onNodeStart, onNodeComplete, onNodeError, onProgressUpdate } = callbacks;
    
    // Find nodes with no dependencies (starting nodes)
    for (const [nodeId, degree] of inDegree.entries()) {
      if (degree === 0) {
        queue.push(nodeId);
      }
    }
    
    while (queue.length > 0) {
      const currentNodeId = queue.shift();
      const { node } = graph.get(currentNodeId);
      
      try {
        // Notify node start
        if (onNodeStart) {
          onNodeStart(currentNodeId);
        }
        
        // Show progress before executing (only if no callbacks provided)
        if (!onNodeStart) {
          const progress = Math.round((completedNodes / totalNodes) * 100);
          toast.info(`⏳ Executing ${node.data.label}... (${progress}% complete)`, { 
            autoClose: 2000,
            toastId: 'workflow-progress' // Prevent duplicate toasts
          });
        }
        
        // Execute current node
        const result = await this.executeNode(node, results);
        results.set(currentNodeId, result);
        completedNodes++;
        
        // Notify node completion
        if (onNodeComplete) {
          onNodeComplete(currentNodeId, result);
        }
        
        // Update progress
        const newProgress = Math.round((completedNodes / totalNodes) * 100);
        if (onProgressUpdate) {
          onProgressUpdate(newProgress);
        }
        
        // Show completion (only if no callbacks provided)
        if (!onNodeComplete && result.output) {
          if (result.type === 'visual' && result.output.imageBase64) {
            toast.success(`✅ ${node.data.label} completed! Image generated. (${newProgress}%)`, { autoClose: 3000 });
          } else if (result.type === 'worksheet' && result.output.worksheets) {
            toast.success(`✅ ${node.data.label} completed! ${result.output.worksheets.length} worksheets generated. (${newProgress}%)`, { autoClose: 3000 });
          } else if (result.type === 'audio' && result.output.audioBase64) {
            toast.success(`✅ ${node.data.label} completed! Audio generated. (${newProgress}%)`, { autoClose: 3000 });
          } else {
            toast.success(`✅ ${node.data.label} completed! (${newProgress}%)`, { autoClose: 2000 });
          }
        }
        
        // Update dependent nodes
        const dependents = graph.get(currentNodeId).dependents;
        for (const dependentId of dependents) {
          inDegree.set(dependentId, inDegree.get(dependentId) - 1);
          if (inDegree.get(dependentId) === 0) {
            queue.push(dependentId);
          }
        }
        
      } catch (error) {
        console.error(`Node ${currentNodeId} execution failed:`, error);
        
        // Notify node error
        if (onNodeError) {
          onNodeError(currentNodeId, error);
        } else {
          toast.error(`❌ ${node.data.label} failed: ${error.message}`, { autoClose: 5000 });
          throw new Error(`Node "${node.data.label}" failed: ${error.message}`);
        }
      }
    }
    
    return results;
  }

  // Legacy method for backwards compatibility
  async executeNodes(executionGraph) {
    return this.executeNodesWithProgress(executionGraph, executionGraph.graph.size);
  }

  async executeNode(node, previousResults) {
    const { type, data } = node;
    
    toast.info(`Executing ${data.label}...`);
    
    switch (type) {
      case 'prompt':
        return this.executePromptNode(node, previousResults);
      
      case 'textGen':
        return this.executeTextGenNode(node, previousResults);
      
      case 'visual':
        return this.executeVisualNode(node, previousResults);
        
      case 'worksheet':
        return this.executeWorksheetNode(node, previousResults);

      case 'mcq':
        return this.executeMCQNode(node, previousResults);

      case 'shortAnswer':
        return this.executeShortAnswerNode(node, previousResults);

      case 'imageAnalysis':
        return this.executeImageAnalysisNode(node, previousResults);
        
      case 'audio':
        return this.executeAudioNode(node, previousResults);
        
      case 'upload':
        return this.executeUploadNode(node, previousResults);
        
      case 'input':
        return this.executeInputNode(node, previousResults);
        
      case 'api':
        return this.executeApiNode(node, previousResults);
        
      case 'offlineLLM':
        return this.executeOfflineLLMNode(node, previousResults);
        
      default:
        throw new Error(`Unknown node type: ${type}`);
    }
  }

  executePromptNode(node, previousResults) {
    const config = node.data.config || {};
    const prompt = config.prompt || 'Default prompt';
    
    // Process variables in the prompt
    let processedPrompt = prompt;
    if (config.variables && config.variables.length > 0) {
      config.variables.forEach(variable => {
        const value = this.getVariableValue(variable, previousResults);
        processedPrompt = processedPrompt.replace(`{{${variable.name}}}`, value);
      });
    }
    
    return {
      type: 'prompt',
      output: processedPrompt,
      timestamp: new Date().toISOString()
    };
  }

  async executeTextGenNode(node, previousResults) {
    const config = node.data.config || {};
    const inputText = this.getPreviousOutput(node.id, previousResults) || config.concept || 'General topic';
    
    console.log('TextGen Node Input:', { inputText, config });
    
    try {
      // Try Modern AI Service first (Gemini 2.5 Flash)
      if (this.modernAIService.isGeminiAvailable()) {
        toast.info('📝 Generating content with Gemini 2.5 Flash...', { autoClose: 3000 });
        
        const response = await this.modernAIService.generateContent(
          inputText,
          config.gradeLevel || '4',
          config.language || 'English'
        );
        
        console.log('Modern AI Response:', response);
        
        return {
          type: 'textGen',
          output: response.data.story || response.data.mainContent || 'Content generated successfully',
          metadata: response.data,
          timestamp: new Date().toISOString()
        };
      }
      
      // Fallback to legacy Gemini direct
      if (this.geminiDirectService.isAvailable()) {
        try {
          toast.info('🔄 Using Gemini Direct service...', { autoClose: 3000 });
          
          const directResponse = await this.geminiDirectService.generateContent(
            inputText,
            config.gradeLevel || '4',
            config.language || 'English'
          );
          
          console.log('Gemini Direct Response:', directResponse);
          
          return {
            type: 'textGen',
            output: directResponse.data.story || directResponse.data.mainContent || 'Content generated successfully',
            metadata: directResponse.data,
            timestamp: new Date().toISOString()
          };
        } catch (directError) {
          console.error('Gemini Direct failed:', directError);
          throw directError;
        }
      }
      
      // Final fallback to backend API
      try {
        const response = await this.apiService.generateContent(
          inputText,
          config.gradeLevel || '4',
          config.language || 'English'
        );
        
        return {
          type: 'textGen',
          output: response.data.story || response.data.content || 'Content generated via backend',
          metadata: response.metadata || response.data,
          timestamp: new Date().toISOString()
        };
      } catch (backendError) {
        console.error('Backend API failed:', backendError);
        
        // Absolute fallback - return a default response
        return {
          type: 'textGen',
          output: `Educational content about "${inputText}" for Grade ${config.gradeLevel || '4'} students in ${config.language || 'English'}. This is a placeholder content generated when all AI services are unavailable. The topic "${inputText}" is an important subject that students can explore further through various educational resources.`,
          metadata: {
            source: 'Fallback Generator',
            model: 'placeholder',
            language: config.language || 'English',
            gradeLevel: config.gradeLevel || '4',
            concept: inputText
          },
          timestamp: new Date().toISOString()
        };
      }
      
    } catch (error) {
      console.error('TextGen Node execution failed:', error);
      
      // Return a user-friendly error result instead of throwing
      return {
        type: 'textGen',
        output: `Unable to generate content about "${inputText}". Error: ${error.message}. Please check your Gemini API key and try again.`,
        metadata: {
          error: true,
          message: error.message,
          source: 'Error Handler',
          concept: inputText,
          gradeLevel: config.gradeLevel || '4',
          language: config.language || 'English'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  async executeVisualNode(node, previousResults) {
    const config = node.data.config || {};
    const inputText = this.getPreviousOutput(node.id, previousResults) || config.concept || '';
    
    try {
      // Try Modern AI Service first (Imagen 4)
      if (this.modernAIService.isGeminiAvailable()) {
        toast.info('🎨 Generating image with Imagen 4...', { autoClose: 5000 });
        
        const response = await this.modernAIService.generateImage(
          inputText,
          { style: config.style || 'educational' }
        );
        
        return {
          type: 'visual',
          output: response.data,
          timestamp: new Date().toISOString()
        };
      }
      
      // Fallback to backend API
      const response = await this.apiService.generateVisual(inputText);
      
      const mappedOutput = {
        imageBase64: response.data.imageBase64,
        svgContent: response.data.svgContent,
        description: response.data.description || inputText,
        format: response.data.format || 'svg',
        mimeType: response.data.mimeType || 'image/svg+xml',
        downloadUrl: response.data.downloadUrl,
        filename: response.data.filename,
        source: response.data.source || 'Backend Generated'
      };
      
      return {
        type: 'visual',
        output: mappedOutput,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.warn('Modern AI and backend failed, trying legacy Gemini:', error);
      
      // Final fallback to legacy Gemini direct
      if (this.geminiDirectService.isAvailable()) {
        try {
          toast.info('🔄 Using legacy Gemini for visual generation...', { autoClose: 3000 });
          
          const directResponse = await this.geminiDirectService.generateImage(
            inputText,
            { style: config.style || 'educational' }
          );
          
          return {
            type: 'visual',
            output: directResponse.data,
            timestamp: new Date().toISOString()
          };
        } catch (directError) {
          throw new Error(`All visual generation methods failed: ${directError.message}`);
        }
      } else {
        throw new Error(`All visual generation services unavailable: ${error.message}`);
      }
    }
  }

  async executeWorksheetNode(node, previousResults) {
    const config = node.data.config || {};
    const inputText = this.getPreviousOutput(node.id, previousResults) || config.concept || 'General topic';
    
    console.log('Worksheet Node Input:', { inputText, config });
    
    try {
      // Try legacy Gemini direct first (simpler implementation)
      if (this.geminiDirectService.isAvailable()) {
        toast.info('📋 Generating worksheets with Gemini Direct...', { autoClose: 3000 });
        
        const directResponse = await this.geminiDirectService.generateWorksheet(
          inputText,
          config.grades || ['3', '4', '5'],
          config.language || 'English'
        );
        
        console.log('Gemini Direct Worksheet Response:', directResponse);
        
        return {
          type: 'worksheet',
          output: directResponse.data,
          timestamp: new Date().toISOString()
        };
      }
      
      // Try Modern AI Service as fallback
      if (this.modernAIService.isGeminiAvailable()) {
        toast.info('📋 Generating worksheets with Modern AI Service...', { autoClose: 5000 });
        
        const response = await this.modernAIService.generateWorksheet(
          inputText,
          config.grades || ['3', '4', '5'],
          config.language || 'English'
        );
        
        console.log('Modern AI Worksheet Response:', response);
        
        return {
          type: 'worksheet',
          output: response.data,
          timestamp: new Date().toISOString()
        };
      }
      
      // Final fallback - create simple worksheet structure
      const grades = config.grades || ['3', '4', '5'];
      const language = config.language || 'English';
      
      const fallbackWorksheets = grades.map(grade => {
        const content = `${inputText} - Grade ${grade} Worksheet\n\nInstructions: Answer the following questions about ${inputText}.\n\n1. What do you know about ${inputText}?\n2. Why is ${inputText} important?\n3. Give an example of ${inputText}.\n4. How does ${inputText} affect our daily life?\n5. What would you like to learn more about ${inputText}?\n\nAnswer Key:\n1. (Student's prior knowledge)\n2. (Educational importance)\n3. (Real-world example)\n4. (Daily life connection)\n5. (Further learning interests)`;
        
        return {
          grade: grade,
          content: content,
          filename: `${inputText.replace(/[^a-zA-Z0-9]/g, '_')}_Grade_${grade}_${language}_${Date.now()}.txt`,
          downloadUrl: `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`,
          format: 'text',
          mimeType: 'text/plain',
          fileSize: content.length,
          source: 'Fallback Generator',
          title: `${inputText} - Grade ${grade} Worksheet`,
          language: language
        };
      });
      
      return {
        type: 'worksheet',
        output: {
          topic: inputText,
          worksheets: fallbackWorksheets,
          totalGenerated: fallbackWorksheets.length,
          language: language,
          grades: grades,
          source: 'Fallback Worksheet Generator',
          method: 'Simple Template Generation'
        },
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Worksheet generation failed:', error);
      
      // Return a user-friendly error result instead of throwing
      return {
        type: 'worksheet',
        output: {
          error: true,
          message: `Unable to generate worksheets for "${inputText}". Error: ${error.message}`,
          topic: inputText,
          worksheets: [],
          totalGenerated: 0,
          language: config.language || 'English',
          grades: config.grades || ['3', '4', '5'],
          source: 'Error Handler'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  async executeAudioNode(node, previousResults) {
    const config = node.data.config || {};
    const inputText = this.getPreviousOutput(node.id, previousResults) || config.text || '';
    
    try {
      // Try Modern AI Service first (ElevenLabs + Indian languages)
      toast.info('🎵 Generating premium audio with ElevenLabs...', { autoClose: 5000 });
      
      const response = await this.modernAIService.generateAudio(
        inputText,
        config.language || 'English',
        {
          stability: config.stability || 0.5,
          similarity_boost: config.similarity_boost || 0.8,
          style: config.style || 0.3
        }
      );
      
      return {
        type: 'audio',
        output: response.data,
        timestamp: new Date().toISOString()
      };
      
    } catch (modernError) {
      console.warn('Modern AI audio failed, trying legacy service:', modernError);
      
      // Fallback to legacy audio service
      try {
        toast.info('🔄 Using legacy audio service...', { autoClose: 3000 });
        
        const audioResponse = await this.audioService.generateAudio(
          inputText,
          config.language || 'English'
        );
        
        return {
          type: 'audio',
          output: audioResponse.data,
          timestamp: new Date().toISOString()
        };
      } catch (audioError) {
        throw new Error(`All audio generation methods failed: ${audioError.message}`);
      }
    }
  }

  async executeUploadNode(node, previousResults) {
    const config = node.data.config || {};
    
    try {
      // Try backend first for image upload and analysis
      const inputData = this.getPreviousOutput(node.id, previousResults);
      
      if (inputData && inputData.includes('base64')) {
        // If we have image data from previous node
        const response = await this.apiService.analyzeTextbookImage(inputData);
        
        return {
          type: 'upload',
          output: {
            analyzedText: response.data.extractedText || 'Image analysis completed',
            summary: response.data.summary || 'Educational content identified',
            keyPoints: response.data.keyPoints || [],
            concepts: response.data.concepts || [],
            gradeLevel: response.data.gradeLevel || 'General',
            subject: response.data.subject || 'General',
            imageData: inputData,
            source: 'Backend Analysis'
          },
          timestamp: new Date().toISOString()
        };
      } else {
        // Fallback to Gemini for image analysis if available
        if (this.geminiDirectService.isAvailable()) {
          try {
            toast.info('🔄 Using Gemini for image analysis...', { autoClose: 3000 });
            
            // Create a demonstration response for image upload
            const placeholderAnalysis = await this.generateImageAnalysisPlaceholder(config);
            
            return {
              type: 'upload',
              output: placeholderAnalysis,
              timestamp: new Date().toISOString()
            };
          } catch (geminiError) {
            console.warn('Gemini image analysis failed:', geminiError);
          }
        }
        
        // Final fallback - return placeholder
        return {
          type: 'upload',
          output: {
            analyzedText: 'File upload functionality requires user interaction. In production, users would drag and drop images here for analysis.',
            summary: 'Image upload and analysis placeholder',
            keyPoints: ['Upload images for textbook analysis', 'Automatic text extraction', 'Educational content identification'],
            concepts: ['Image Recognition', 'OCR', 'Educational Analysis'],
            gradeLevel: 'Interactive',
            subject: 'System Feature',
            imageData: null,
            source: 'Demo Placeholder'
          },
          timestamp: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('Upload node execution failed:', error);
      return {
        type: 'upload',
        output: {
          analyzedText: 'Upload analysis failed - backend services may be unavailable',
          summary: 'Error in image analysis',
          error: error.message,
          source: 'Error Handler'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  // Generate demonstration image analysis
  async generateImageAnalysisPlaceholder(config) {
    const model = this.geminiDirectService.genAI?.getGenerativeModel({ model: 'gemini-1.5-flash' });
    
    if (!model) {
      return {
        analyzedText: 'Image upload demonstration - would extract text from uploaded images',
        summary: 'Educational image analysis placeholder',
        keyPoints: ['Text extraction from images', 'Content summarization', 'Educational concept identification'],
        concepts: ['Image Processing', 'OCR', 'Content Analysis'],
        gradeLevel: 'Demo',
        subject: 'Technology',
        imageData: null,
        source: 'Placeholder Generator'
      };
    }

    try {
      const prompt = `Generate a realistic example of what would happen when a student uploads an educational textbook page image. 
      
      Create a sample analysis that includes:
      - Extracted text (realistic educational content)
      - Summary of the content
      - Key educational concepts
      - Appropriate grade level
      - Subject identification
      
      Make it educational and realistic for a ${config.fileType || 'general'} textbook page.`;

      const result = await model.generateContent(prompt);
      const analysis = result.response.text();
      
      return {
        analyzedText: analysis,
        summary: 'AI-generated example of textbook image analysis',
        keyPoints: ['Automatic text extraction', 'Content summarization', 'Educational analysis'],
        concepts: ['Image Recognition', 'Machine Learning', 'Educational Technology'],
        gradeLevel: 'Demonstration',
        subject: 'AI Example',
        imageData: null,
        source: 'Gemini Analysis Demo'
      };
    } catch (error) {
      return {
        analyzedText: 'Gemini analysis demonstration - would provide detailed content extraction',
        summary: 'AI-powered image analysis example',
        keyPoints: ['Smart text recognition', 'Educational content analysis', 'Automatic summarization'],
        concepts: ['AI', 'Computer Vision', 'Education Technology'],
        gradeLevel: 'Demo',
        subject: 'Technology',
        imageData: null,
        source: 'Fallback Demo'
      };
    }
  }

  executeInputNode(node, previousResults) {
    const config = node.data.config || {};
    
    // For input nodes, we'll return a placeholder since it requires user interaction
    return {
      type: 'input',
      output: config.placeholder || 'User input required',
      timestamp: new Date().toISOString()
    };
  }

  async executeApiNode(node, previousResults) {
    const config = node.data.config || {};
    const inputData = this.getPreviousOutput(node.id, previousResults);
    
    try {
      let response;
      let attemptCount = 0;
      const maxRetries = 3;
      
      // Handle different API types with retry logic
      while (attemptCount < maxRetries) {
        try {
          switch (config.apiType) {
            case 'wikipedia':
              const query = inputData || config.query || 'education';
              response = await this.externalApiService.searchWikipedia(query, config.language || 'en');
              break;
              
            case 'country':
              const countryName = inputData || config.countryName || 'India';
              response = await this.externalApiService.getCountryInfo(countryName);
              break;
              
            case 'weather':
              if (!config.apiKey) throw new Error('Weather API key required');
              const location = inputData || config.location || 'Mumbai';
              response = await this.externalApiService.getWeatherData(location, config.apiKey);
              break;
              
            case 'news':
              if (!config.apiKey) throw new Error('News API key required');
              const topic = inputData || config.topic || 'education';
              response = await this.externalApiService.getNewsData(topic, config.apiKey, config.language);
              break;
              
            case 'quotes':
              const category = inputData || config.category || 'education';
              response = await this.externalApiService.getQuotesData(category);
              break;
              
            case 'jokes':
              response = await this.externalApiService.getEducationalJoke();
              break;
              
            case 'nasa':
              if (!config.apiKey) throw new Error('NASA API key required');
              response = await this.externalApiService.getNasaData(config.apiKey, config.endpoint);
              break;
              
            case 'custom':
              if (!config.url) throw new Error('API URL is required for custom calls');
              response = await this.externalApiService.callCustomAPI(
                config.url,
                config.method || 'GET',
                inputData || config.body,
                config.headers || {}
              );
              break;
              
            default:
              // Legacy support - try to detect API type from URL
              if (config.url) {
                if (config.url.includes('wikipedia.org')) {
                  const query = inputData || config.query || 'education';
                  response = await this.externalApiService.searchWikipedia(query);
                } else {
                  response = await this.externalApiService.callCustomAPI(
                    config.url,
                    config.method || 'GET',
                    inputData || config.body,
                    config.headers || {}
                  );
                }
              } else {
                throw new Error('API configuration is incomplete');
              }
          }
          
          // If we get here, the API call was successful
          break;
          
        } catch (apiError) {
          attemptCount++;
          console.warn(`API call attempt ${attemptCount} failed:`, apiError);
          
          if (attemptCount >= maxRetries) {
            throw apiError;
          }
          
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attemptCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          toast.info(`🔄 API call failed, retrying... (${attemptCount}/${maxRetries})`, { autoClose: 2000 });
        }
      }
      
      return {
        type: 'api',
        output: response,
        apiType: config.apiType || 'custom',
        timestamp: new Date().toISOString(),
        metadata: {
          attempts: attemptCount + 1,
          source: 'External API'
        }
      };
      
    } catch (externalError) {
      console.warn('All external API attempts failed, trying Gemini fallback:', externalError);
      
      // Fallback to Gemini direct for content generation
      if (this.geminiDirectService.isAvailable() && ['wikipedia', 'country', 'quotes', 'jokes'].includes(config.apiType)) {
        try {
          toast.info('🔄 External API failed, using Gemini AI fallback...', { autoClose: 3000 });
          
          // Generate content using Gemini based on the API type
          let fallbackPrompt = '';
          switch (config.apiType) {
            case 'wikipedia':
              const query = inputData || config.query || 'education';
              fallbackPrompt = `Provide detailed educational information about "${query}" as if from an encyclopedia. Include key facts, history, and significance.`;
              break;
            case 'country':
              const countryName = inputData || config.countryName || 'India';
              fallbackPrompt = `Provide comprehensive information about the country "${countryName}" including geography, culture, history, and interesting facts.`;
              break;
            case 'quotes':
              const category = inputData || config.category || 'education';
              fallbackPrompt = `Generate 3-5 inspirational quotes related to "${category}" with their attributed authors.`;
              break;
            case 'jokes':
              fallbackPrompt = 'Generate a clean, educational joke suitable for students that is both funny and informative.';
              break;
          }
          
          const fallbackResponse = await this.geminiDirectService.generateContent(
            fallbackPrompt,
            '4',
            'English'
          );
          
          return {
            type: 'api',
            output: {
              content: fallbackResponse.data,
              source: 'Gemini AI Fallback',
              note: 'External API unavailable, content generated by AI'
            },
            apiType: config.apiType || 'custom',
            timestamp: new Date().toISOString(),
            metadata: {
              source: 'Gemini Fallback',
              originalError: externalError.message
            }
          };
          
        } catch (fallbackError) {
          // Final fallback - return error information but don't fail completely
          return {
            type: 'api',
            output: {
              error: true,
              message: `Both external API and Gemini fallback failed`,
              externalError: externalError.message,
              fallbackError: fallbackError.message,
              suggestion: 'Please check your API configuration and network connection'
            },
            apiType: config.apiType || 'custom',
            timestamp: new Date().toISOString(),
            metadata: {
              source: 'Error Response',
              failed: true
            }
          };
        }
      } else {
        // For APIs that don't have Gemini fallback (weather, news, nasa, custom)
        return {
          type: 'api',
          output: {
            error: true,
            message: `External API "${config.apiType}" failed after ${maxRetries} attempts`,
            details: externalError.message,
            suggestion: config.apiType === 'custom' ? 
              'Check your custom API URL and configuration' :
              `Check your ${config.apiType} API key and configuration`
          },
          apiType: config.apiType || 'custom',
          timestamp: new Date().toISOString(),
          metadata: {
            source: 'Error Response',
            failed: true,
            attempts: maxRetries
          }
        };
      }
    }
  }

  async executeOfflineLLMNode(node, previousResults) {
    const config = node.data.config || {};
    const inputText = this.getPreviousOutput(node.id, previousResults) || config.prompt || '';
    
    try {
      toast.info('🤖 Calling vidya-agent backend...', { autoClose: 3000 });
      
      // Vidya-agent specific configuration
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL || config.apiUrl || 'http://localhost:3001/api';
      const endpoint = config.endpoint || 'generate-content'; // Changed to match vidya-agent endpoint
      
      // Format request payload for vidya-agent API
      const requestPayload = {
        concept: inputText, // vidya-agent expects 'concept' instead of 'prompt'
        gradeLevel: config.gradeLevel || '4',
        language: config.language || 'English'
      };
      
      console.log('Calling vidya-agent API:', `${backendUrl}/${endpoint}`, requestPayload);
      
      const response = await fetch(`${backendUrl}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Vidya-agent API error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Vidya-agent response:', data);
      
      // Handle vidya-agent response format
      if (data.status === 'success') {
        return {
          type: 'offlineLLM',
          output: {
            generatedText: data.data.story || 'Generated content from vidya-agent',
            model: data.metadata?.model || 'vidya-agent',
            tokens: data.metadata?.tokens || null,
            processingTime: data.metadata?.processingTime || null,
            generationTime: data.metadata?.generationTime || null,
            cached: data.metadata?.cached || false,
            metadata: data.metadata || {},
            source: 'Vidya-Agent Backend',
            apiUrl: `${backendUrl}/${endpoint}`,
            timestamp: new Date().toISOString()
          },
          timestamp: new Date().toISOString()
        };
      } else {
        throw new Error(`Vidya-agent returned error: ${data.message || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('Vidya-agent connection failed:', error);
      
      // Enhanced error information for debugging
      return {
        type: 'offlineLLM',
        output: {
          error: true,
          message: `Vidya-agent connection failed: ${error.message}`,
          suggestion: `
            Troubleshooting steps:
            1. Make sure vidya-agent is running on port 3001
            2. Check if http://localhost:3001/api/health is accessible
            3. Verify CORS is enabled in vidya-agent
            4. Check console logs for detailed error information
          `,
          inputText: inputText,
          config: config,
          attemptedUrl: `http://localhost:3001/api/generate-content`,
          source: 'Vidya-Agent Error Handler'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  async executeMCQNode(node, previousResults) {
    const config = node.data.config || {};
    const inputText = this.getPreviousOutput(node.id, previousResults) || config.concept || 'General topic';
    
    console.log('MCQ Node Input:', { inputText, config });
    
    try {
      const result = await this.mcqService.generateMCQs(
        inputText,
        config.grade || 5,
        config.language || 'English',
        config.count || 5
      );
      
      return {
        type: 'mcq',
        data: result.data,
        output: result.data,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('MCQ generation failed:', error);
      return {
        type: 'mcq',
        output: {
          error: true,
          message: `MCQ generation failed: ${error.message}`,
          source: 'MCQ Service Error Handler'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  async executeShortAnswerNode(node, previousResults) {
    const config = node.data.config || {};
    const inputText = this.getPreviousOutput(node.id, previousResults) || config.concept || 'General topic';
    
    console.log('Short Answer Node Input:', { inputText, config });
    
    try {
      const result = await this.shortAnswerService.generateShortAnswerQuestions(
        inputText,
        config.grade || 5,
        config.language || 'English',
        config.count || 5
      );
      
      return {
        type: 'shortAnswer',
        data: result.data,
        output: result.data,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Short answer generation failed:', error);
      return {
        type: 'shortAnswer',
        output: {
          error: true,
          message: `Short answer generation failed: ${error.message}`,
          source: 'Short Answer Service Error Handler'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  async executeImageAnalysisNode(node, previousResults) {
    const config = node.data.config || {};
    
    // Try to get image from upload node or config
    let imageBase64 = config.imageBase64;
    if (!imageBase64) {
      // Look for image in previous results
      const resultArray = Array.from(previousResults.values());
      for (const result of resultArray) {
        if (result.type === 'upload' && result.output?.imageBase64) {
          imageBase64 = result.output.imageBase64;
          break;
        }
      }
    }
    
    if (!imageBase64) {
      return {
        type: 'imageAnalysis',
        output: {
          error: true,
          message: 'No image provided for analysis. Connect an upload node or provide image in configuration.',
          source: 'Image Analysis Service Error Handler'
        },
        timestamp: new Date().toISOString()
      };
    }
    
    console.log('Image Analysis Node Input:', { config, hasImage: !!imageBase64 });
    
    try {
      const result = await this.imageAnalysisService.analyzeEducationalImage(
        imageBase64,
        config.grade || 5,
        config.language || 'English'
      );
      
      return {
        type: 'imageAnalysis',
        data: result.data,
        output: result.data,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Image analysis failed:', error);
      return {
        type: 'imageAnalysis',
        output: {
          error: true,
          message: `Image analysis failed: ${error.message}`,
          source: 'Image Analysis Service Error Handler'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  getPreviousOutput(nodeId, results) {
    // For now, get the output from the most recent result
    // In a more sophisticated implementation, we'd trace back through the graph
    const resultArray = Array.from(results.values());
    if (resultArray.length > 0) {
      const lastResult = resultArray[resultArray.length - 1];
      return typeof lastResult.output === 'string' ? lastResult.output : JSON.stringify(lastResult.output);
    }
    return null;
  }

  getVariableValue(variable, results) {
    // Simple variable resolution - can be enhanced
    return results.get(variable.nodeId)?.output || variable.defaultValue || '';
  }
}

export default WorkflowEngine;